<template>
	<div class="login">
		<div
			class="login__container"
			:class="{ 'login__container--visible': setup.showLogoAnimation }">
			<animated-logo-armado
				class="login__logo"
				:active="setup.showLogoAnimation"/>

			<div
				v-if="!setup.needFpValidation"
				class="login__form"
				:class="{ 'login__form--visible': setup.showForm }"
				@keyup.enter="setup.loginAsync()">
				<div class="form flex fd-c g-sm">
					<div class="flex ai-c g-sm">
						<o-input
							:ref="setup._email"
							v-model="setup.user.username"
							:disabled="setup.showPassword"
							type="email"
							label="Email"
							:validation="setup.validation.rule('username')"
							required
							force-label-floating
							style="flex:1"
							@keydown.enter.stop="setup.checkSSOAsync()"/>
						<o-icon
							v-if="setup.showPassword"
							v-tooltip="`Modifier l'email`"
							class="mr-xs"
							ripple="info"
							font-icon="icon-interface-edit-pencil"
							@click="setup.resetLogin()"/>
					</div>

					<o-password
						v-if="setup.needSSO === false && setup.showPassword === true"
						:ref="setup._password"
						v-model="setup.user.password"
						:validation="setup.validation.rule('password')"
						type="password"
						label="Mot de passe"
						required
						force-label-floating/>
					<input
						v-else
						type="password"
						class="hidden-password-field"
						label="Mot de passe">
				</div>

				<div
					v-if="setup.errorMessage || setup.accountLocked || setup.route.query.error"
					class="login__alert"
					:style="setup.accountLocked ? 'margin: 0px -50px;' : ''">
					<o-alert
						v-if="setup.errorMessage && !setup.accountLocked"
						color="warning">
						{{ setup.errorMessage }}
					</o-alert>
					<o-alert
						v-if="setup.accountLocked"
						color="warning">
						Votre compte a été bloqué <b>pour une durée de 30 minutes</b> suite à de multiples tentatives incorrectes.
						<br>
						Réessayez plus tard ou cliquez sur 'Mot de passe oublié'
					</o-alert>
					<o-alert
						v-if="setup.interpretError()"
						class="mt-xs"
						color="warning">
						{{ setup.interpretError() }}
					</o-alert>
				</div>

				<div class="login__actions">
					<o-button
						v-if="!setup.showPassword"
						color="brand"
						@click="setup.checkSSOAsync()">
						Suivant
					</o-button>

					<o-button
						v-if="setup.showPassword"
						color="brand"
						@click="setup.loginAsync()">
						Se connecter {{ setup.needSSO ? ` avec la Suite Enso` : undefined }}
					</o-button>

					<o-button
						color="brand"
						nude
						@click="$router.push({ name: 'LoginReset', query: { email: setup.user.username } })">
						Mot de passe oublié
					</o-button>

					<hr>

					<o-button
						color="brand"
						nude
						block
						@click="$router.push({ name: 'Signup' })">
						Créer mon espace candidat
					</o-button>
				</div>
			</div>

			<div
				v-else-if="setup.needFpValidation"
				title="Validation de votre appareil">
				<o-section>
					<o-otp
						:ref="setup._otp"
						@filled="setup.checkCodeAsync($event)"/>
				</o-section>

				<o-section gap="sm">
					<o-alert
						v-if="!!setup.errorMessage"
						color="danger"
						center>
						{{ setup.errorMessage }}
					</o-alert>

					<o-alert
						v-if="setup.fpCodeSentByMail"
						class="mv-sm">
						Saisissez ci-dessus le code que nous vous avons envoyé<br>
						{{ setup.fpCodeSentByMail ? `par email à l'adresse` : `par sms au numéro` }}
						<span
							v-if="setup.fpCodeSentByMail"
							class="text--info">{{ setup.user.username }}</span>
						<span
							v-else
							class="text--info">••••••••{{ setup.phoneNumber?.mobilePhone }}</span>
					</o-alert>
				</o-section>

				<p class="text--center">
					<strong>Renvoyer un nouveau code par :</strong>
				</p>

				<div class="fp__actions">
					<o-button
						color="info"
						prefix-font-icon="icon-mail-send-email-envelope"
						outline
						:loading="setup.fpSendingRequest"
						@click="setup.resendCodeAsync()">
						Email
					</o-button>
					<o-button
						v-if="setup.phoneNumber"
						color="info"
						prefix-font-icon="icon-phone-mobile-phone"
						outline
						:loading="setup.fpSendingRequest"
						@click="setup.resendSmsCodeAsync()">
						Sms ••••••••{{ setup.phoneNumber.mobilePhone }}
					</o-button>
				</div>
			</div>
		</div>

		<div class="login__poster">
			<!-- <img
				src="@/assets/img/pexels-doni-haris-2968939.jpg"
				alt="Digital"> -->
			<div
				v-for="i in 4"
				:key="i"
				class="login__poster-circle"/>

			<div class="login__poster-content">
				<div class="login__poster-img">
					<div class="login__poster-img-contract">
						Contrat
						<div
							v-for="i in 2"
							:key="i"
							class="login__poster-img-contract-line login__poster-img-contract-line--header"/>
						<div
							v-for="i in 3"
							:key="i"
							class="login__poster-img-contract-line login__poster-img-contract-line--core"/>

						<div class="login__poster-img-contract-actions">
							<div
								v-for="i in 2"
								:key="i"
								class="login__poster-img-buttons"/>
						</div>

						<div
							v-for="i in 3"
							:key="i"
							class="login__poster-img-sticker"
							:class="[
								{ 'login__poster-img-sticker--agency':i === 1 },
								{ 'login__poster-img-sticker--client':i === 2 },
								{ 'login__poster-img-sticker--tempworker':i === 3 },
							]">
							<template v-if="i === 1">
								<o-chips color="danger">Agence</o-chips>
								<o-icon font-icon="icon-shopping-building"/>
								<p>Dématérialisation de contrats</p>
								<img
									src="@/assets/img/login/agency-arrow.svg"
									alt="">
							</template>
							<template v-if="i === 2">
								<o-chips color="info">Entreprise</o-chips>
								<o-icon font-icon="icon-shopping-store-phone"/>
								<p>Validation et Signature électronique</p>
								<img
									src="@/assets/img/login/client-arrow.svg"
									alt="">
							</template>
							<template v-if="i === 3">
								<o-chips color="inverse">Intérimaire</o-chips>
								<o-icon font-icon="icon-user-single-neutral"/>
								<p>Signature électronique</p>
								<img
									src="@/assets/img/login/temp-arrow.svg"
									alt="">
							</template>
						</div>
					</div>
					<div class="login__poster-text">
						<h3>L'intérim digital</h3>
						<p>
							<strong>Agence d'emplois, entreprise utilisatrice, ou intérimaire,</strong>
							gagnez du temps grâce à notre outil de dématérialisation et de signature électronique
						</p>
					</div>
				</div>
			</div>

		<!-- <div class="news__container">
			<template v-if="setup.state.dataLoaded">
				<div v-if="setup.state.news">
					<img
						v-if="setup.state.news.thumbnail"
						:src="setup.state.news.thumbnail.url"
						:alt="setup.state.news.title"
						class="news__thumbnail">
					<h1 class="news__title">{{ setup.state.news.title }}</h1>
					<div
						class="news__content"
						v-html="setup.state.news.content"/>
				</div>
				<div v-else>
					<h1 class="news__title">
						<span class="text--grey">L’intérim digital pensé pour</span><br>
						les intérimaires,<br>
						les agences,<br>
						les entreprises
					</h1>
					<br>
					<img
						src="@Img/illustration/digital-interim.svg"
						alt="L'intérim digital">
				</div>
			</template>
			<o-loader :visible="!setup.state.dataLoaded"/>
		</div> -->
		</div>
	</div>
</template>

<script setup lang="ts">
import AnimatedLogoArmado from '../AnimatedLogoArmado.vue';
import LoginFormSetupService from '@/setup/login/LoginFormSetupService';
const props = defineProps(LoginFormSetupService.props);
const setup = new LoginFormSetupService(props);
</script>

<style scoped lang="less">
.fp__actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 0 -2rem;
}

.hidden-password-field {
  opacity: 0;
  height: 0;
  pointer-events: none;
  position: absolute;
}
</style>
